import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API_URL } from "../../config";
import { authHeaders, feeByLoadAmount, fetchFeeQuote, usd } from "./feeTiers";

export default function RateConUploadModal({
  row,
  feeTiersText,
  onCancel,
  onConfirmed,
}) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [negotiatedRate, setNegotiatedRate] = useState(
    row.negotiated_rate_cents ? String(Math.round(row.negotiated_rate_cents / 100)) : ""
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [quote, setQuote] = useState(null);

  // The backend's fee for the rate being typed, so ops don't work out the tier.
  useEffect(() => {
    setQuote(null);
    const rate = parseInt(negotiatedRate, 10);
    if (Number.isNaN(rate) || rate < 50 || rate > 50000) return undefined;
    let cancelled = false;
    const id = setTimeout(() => {
      fetchFeeQuote(row.booking_request_id, rate * 100).then((q) => {
        if (!cancelled) setQuote(q);
      });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [negotiatedRate, row.booking_request_id]);

  const existingFiles = row.rate_confirmation_files || [];
  const postedDollars = row.posted_rate_cents ? Math.round(row.posted_rate_cents / 100) : null;

  const snap = row.load_snapshot || {};
  const o = `${snap.oc || ""}, ${snap.os || ""}`.replace(/^,\s*|,\s*$/g, "");
  const d = `${snap.dc || ""}, ${snap.ds || ""}`.replace(/^,\s*|,\s*$/g, "");
  const lane = !o && !d ? "" : `${o || "?"} → ${d || "?"}`;

  async function submit() {
    setError("");
    if (!file) {
      setError("Please choose a PDF file.");
      return;
    }
    if (file.type !== "application/pdf") {
      setError("Only PDF files are accepted.");
      return;
    }
    const rateNum = parseInt(negotiatedRate, 10);
    if (Number.isNaN(rateNum) || rateNum < 50 || rateNum > 50000) {
      setError("Negotiated rate must be a whole dollar amount between $50 and $50,000.");
      return;
    }

    setBusy(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("negotiated_rate", String(rateNum));

      await axios.post(
        `${API_URL}/book-it/admin/${row.booking_request_id}/rate-confirmation`,
        formData,
        {
          headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
        }
      );
      onConfirmed();
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed. Please try again.");
      setBusy(false);
    }
  }

  return (
    <div
      onClick={busy ? undefined : onCancel}
      className="fixed inset-0 z-[1000] flex items-center justify-center px-5 bg-slate-900/60 backdrop-blur-[2px]"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl p-7 w-full max-w-md shadow-2xl text-slate-900"
      >
        <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-3">
          Upload rate confirmation
        </h2>

        <div className="bg-slate-50 border border-slate-200 rounded-lg px-3.5 py-3 mb-4">
          <div className="flex items-baseline justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[11px] uppercase font-semibold tracking-wider text-slate-500 mb-0.5">
                Load
              </div>
              <div className="text-sm font-semibold text-slate-900 truncate">
                {row.trucker_name || "—"}{lane ? ` · ${lane}` : ""}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[11px] uppercase font-semibold tracking-wider text-slate-500 mb-0.5">
                Posted rate
              </div>
              <div className="text-sm font-semibold text-slate-900">
                {postedDollars ? `$${postedDollars.toLocaleString()}` : "TBD"}
              </div>
            </div>
          </div>
        </div>

        <p className="text-sm text-slate-600 leading-relaxed mb-4">
          Attach the broker's rate confirmation PDF and enter the final negotiated
          rate. The flat fee is set {feeByLoadAmount(feeTiersText)} and will
          be calculated from the negotiated rate.
        </p>

        <label className="block text-sm font-medium text-slate-600 mb-3">
          Rate confirmation PDF
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="hidden"
          />
          <div className="mt-1.5 flex items-center gap-3">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="!shadow-none !bg-indigo-50 !text-indigo-700 !border !border-indigo-200 hover:!bg-indigo-100 hover:!border-indigo-400 !rounded-lg !px-3 !py-2 !text-sm !font-semibold whitespace-nowrap"
            >
              Choose PDF
            </button>
            <span className="text-sm text-slate-500 truncate min-w-0">
              {file ? file.name : "No file chosen"}
            </span>
          </div>
        </label>

        <label className="block text-sm font-medium text-slate-600 mb-4">
          Negotiated rate (whole dollars)
          <div className="mt-1.5 relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
              $
            </span>
            <input
              type="number"
              min={50}
              max={50000}
              step={1}
              value={negotiatedRate}
              onChange={(e) => setNegotiatedRate(e.target.value)}
              placeholder={postedDollars ? `e.g. ${postedDollars}` : "e.g. 2650"}
              className="w-full pl-7 pr-3 py-2.5 border border-slate-300 rounded-lg text-sm bg-white text-slate-900 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
            />
          </div>
          {quote && (
            <span className="text-xs font-semibold text-slate-700 mt-1.5 block">
              {quote.charged
                ? `Fee already charged: ${usd(quote.fee_cents)}`
                : quote.fee_cents === 0
                  ? "Books on their free credit — no fee"
                  : `Fee at this rate: ${usd(quote.fee_cents)}`}
            </span>
          )}
          {postedDollars && (
            <span className="text-xs text-slate-500 mt-1.5 block">
              Posted rate is ${postedDollars.toLocaleString()} — enter the actual negotiated rate
              with the broker.
            </span>
          )}
        </label>

        {existingFiles.length > 0 && (
          <div className="mb-4">
            <p className="text-xs uppercase font-semibold text-slate-500 tracking-wider mb-2">
              Previously uploaded ({existingFiles.length})
            </p>
            <ul className="space-y-1 text-xs text-slate-600">
              {existingFiles.map((f, i) => (
                <li key={i} className="flex justify-between">
                  <span className="truncate">{f.filename}</span>
                  <span className="text-slate-400">
                    {f.uploaded_at ? new Date(f.uploaded_at).toLocaleDateString() : ""}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-slate-400 mt-2">
              New uploads are added — they don't replace existing files.
            </p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3.5 py-2.5 text-sm mb-3">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2.5">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="!shadow-none !bg-white !text-slate-700 !border !border-slate-300 hover:!bg-slate-50 !rounded-lg !px-4 !py-2.5 !text-sm !font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="!shadow-none !bg-indigo-600 hover:!bg-indigo-700 !text-white !border-0 !rounded-lg !px-4 !py-2.5 !text-sm !font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {busy ? "Uploading…" : "Upload & save rate"}
          </button>
        </div>
      </div>
    </div>
  );
}
